<template>
  <div class="manage-exams-view">
    <!-- Create Exam View -->
    <transition
      name="exam-view"
      mode="out-in"
      enter-active-class="exam-view-enter-active"
      leave-active-class="exam-view-leave-active"
      enter-from-class="exam-view-enter-from"
      leave-to-class="exam-view-leave-to"
    >
      <CreateExamView 
        v-if="currentView === 'create'" 
        @back="currentView = 'list'"
        @exam-created="fetchExams"
        key="create-exam"
      />
 
      <!-- Edit Exam View -->
      <EditExamView 
        v-else-if="currentView === 'edit' && selectedExamId > 0" 
        :exam-id="selectedExamId"
        @back="currentView = 'list'"
        @exam-updated="fetchExams"
        key="edit-exam"
      />

      <!-- Main List View -->
      <div v-else key="list-view">
      <!-- Page Header -->
      <div class="mb-6">
        <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Exams
        </h1>
        <p class="text-secondary-text dark:text-secondary-dark-text">
          Click on an exam to view details such as questions and answers.
        </p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="mt-6 flex flex-col justify-center items-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
        <span class="text-gray-900 dark:text-white">Loading exams...</span>
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="mt-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
      >
        <div class="flex items-center">
          <AlertCircle class="h-5 w-5 text-red-500 mr-2" />
          <span class="text-red-700 dark:text-red-300 font-medium">{{ error }}</span>
        </div>
        <button
          @click="fetchExams"
          class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
        >
          Try again
        </button>
      </div>

      <!-- Main Content -->
      <div v-else class="space-y-6">
        <!-- Quick Actions Section -->
        <div class="grid grid-cols-1 gap-6">
          <!-- View Exam Results -->
          <router-link to="/exam-results" class="block">
            <div
              class="card p-6 hover:shadow-md transition-shadow cursor-pointer border-2 border-transparent hover:border-purple-200 dark:hover:border-purple-800"
              style="height:100%"
            >
              <div class="flex items-center mb-4">
                <div class="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mr-4">
                  <BarChart3 class="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                  View Exam Results
                </h3>
              </div>
              <p class="text-secondary-text dark:text-secondary-dark-text text-sm">
                Preview and review exam content and structure.
              </p>
            </div>
          </router-link>
        </div>

        <!-- Statistics Cards -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="card p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <ClipboardList class="w-8 h-8 text-blue-500" />
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-secondary-text dark:text-secondary-dark-text">
                  Total Exams
                </p>
                <p class="text-2xl font-bold text-gray-900 dark:text-white">
                  {{ totalExams }}
                </p>
              </div>
            </div>
          </div>

          <div class="card p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <CheckCircle class="w-8 h-8 text-green-500" />
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-secondary-text dark:text-secondary-dark-text">
                  Active Exams
                </p>
                <p class="text-2xl font-bold text-gray-900 dark:text-white">
                  {{ activeExams }}
                </p>
              </div>
            </div>
          </div>

          <div class="card p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <TrendingUp class="w-8 h-8 text-orange-500" />
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-secondary-text dark:text-secondary-dark-text">
                  Avg. Questions
                </p>
                <p class="text-2xl font-bold text-gray-900 dark:text-white">
                  {{ averageQuestions }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Exam List -->
        <DataTable
          :data="displayExams"
          :columns="tableColumns"
          :loading="loading"
          :sortable-columns="sortableColumns"
          :mobile-columns="mobileColumns"
          item-key="exam_id"
          item-label="exams"
          search-placeholder="Search exams..."
          :summary-icon="ClipboardList"
          :show-search="true"
          :show-filters="true"
          :show-pagination="true"
          :show-summary="true"
          :items-per-page="5"
          :items-per-page-options="[5, 10, 15, 25, 50]"
          table-id="manage-exams-table"
          @search="handleSearch"
          @sort="handleSort"
          @filter="handleFilter"
          @page-change="handlePageChange"
          @items-per-page-change="handleItemsPerPageChange"
        >
          <!-- Custom action buttons -->
          <template #actions>
            <!-- <button @click="currentView = 'create'" class="btn btn-primary btn-sm flex items-center gap-2">
              <Plus class="h-4 w-4" />
              <span class="hidden sm:inline">Create Exam</span>
            </button> -->
          </template>

          <!-- Custom filters -->
          <template #filters="{ updateFilter }">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Lesson
              </label>
              <select
                v-model="lessonFilter"
                @change="updateFilter('lesson_id', lessonFilter === '' ? '' : Number(lessonFilter))"
                class="block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200 shadow-sm hover:shadow-md"
              >
                <option value="">All Lessons</option>
                <option
                  v-for="mod in sortedLessons"
                  :key="mod.lesson_id"
                  :value="mod.lesson_id"
                >
                  {{ mod.name }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                v-model="statusFilter"
                @change="updateFilter('exam_status', statusFilter === '' ? '' : Number(statusFilter))"
                class="block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200 shadow-sm hover:shadow-md"
              >
                <option value="">All Status</option>
                <option value="1">Active</option>
                <option value="0">Draft</option>
              </select>
            </div>
          </template>

          <!-- Custom cell content -->
          <template #cell-lesson="{ item }">
            <span v-if="item.lesson" class="text-sm font-medium text-gray-900 dark:text-white">
              {{ formatLessonName(item.lesson) }}
            </span>
            <span v-else class="text-sm text-gray-400">No lesson</span>
          </template>

          <template #cell-exam_name="{ item }">
            <span
              :class="[
                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                item.is_final_quiz
                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
                  : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
              ]"
            >
              {{ item.exam_name }}
            </span>
          </template>

          <template #cell-questions="{ item }">
            {{ item.exam_content?.questions?.length || 0 }}
          </template>

          <template #cell-status="{ item }">
            <div class="flex justify-center">
              <span
                :class="[
                  'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                  item.exam_status === 1
                    ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100'
                ]"
              >
                {{ item.exam_status === 1 ? 'Active' : 'Draft' }}
              </span>
            </div>
          </template>

          <!-- Row actions -->
          <template #row-actions="{ item }">
            <div class="flex justify-center space-x-3">
              <!-- Status Toggle -->
              <!-- <button
                :class="[
                  'transition duration-200 transform',
                  item.exam_status === 1
                    ? 'text-orange-500 hover:text-orange-700'
                    : 'text-green-500 hover:text-green-700'
                ]"
                @click="toggleExamStatus(item)"
                :title="item.exam_status === 1 ? 'Deactivate Exam' : 'Activate Exam'"
              >
                <LockOpen v-if="item.exam_status === 1" class="h-5 w-5" />
                <Lock v-else class="h-5 w-5" />
              </button> -->

              <!-- Edit Button -->
              <!-- <button
                class="text-blue-500 hover:text-blue-700"
                @click="editExam(item)"
                title="Edit Exam"
              >
                <Edit class="h-5 w-5" />
              </button> -->

              <!-- View Button -->
              <button
                class="text-green-500 hover:text-green-700"
                @click="viewExam(item)"
                title="View Exam"
              >
                <Eye class="h-5 w-5" />
              </button>

              <!-- Delete Button -->
              <!-- <button
                class="text-red-500 hover:text-red-700"
                @click="deleteExam(item)"
                title="Delete Exam"
              >
                <Trash2 class="h-5 w-5" />
              </button> -->
            </div>
          </template>

          <!-- Mobile actions -->
          <template #mobile-actions="{ item }">
            <div class="flex gap-2">
              <!-- <button
                @click="toggleExamStatus(item)"
                :class="[
                  'p-2 rounded-lg transition-colors',
                  item.exam_status === 1
                    ? 'text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20'
                    : 'text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20'
                ]"
                :title="item.exam_status === 1 ? 'Deactivate' : 'Activate'"
              >
                <LockOpen v-if="item.exam_status === 1" class="h-4 w-4" />
                <Lock v-else class="h-4 w-4" />
              </button>
              <button
                @click="editExam(item)"
                class="p-2 rounded-lg text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                title="Edit"
              >
                <Edit class="h-4 w-4" />
              </button> -->
              <button
                @click="viewExam(item)"
                class="p-2 rounded-lg text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                title="View"
              >
                <Eye class="h-4 w-4" />
              </button>
              <!-- <button
                @click="deleteExam(item)"
                class="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                title="Delete"
              >
                <Trash2 class="h-4 w-4" />
              </button> -->
            </div>
          </template>

          <!-- Mobile cell customizations -->
          <template #mobile-lesson="{ item }">
            <span v-if="item.lesson" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
              {{ item.lesson.name }}
            </span>
            <span v-else class="text-gray-400">No lesson</span>
          </template>

          <!-- Complete custom mobile card layout -->
          <template #mobile-card="{ item }">
            <div class="bg-white dark:bg-gray-800 rounded-lg p-4 shadow border border-gray-200 dark:border-gray-700">
              <!-- Header with exam name and status -->
              <div class="flex items-start justify-between mb-3">
                <div class="flex-1">
                    <p class="text-sm text-secondary-text dark:text-secondary-dark-text mb-1">
                      {{ item.lesson?.name || 'No lesson assigned' }}
                    </p>
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white">
                      {{ item.exam_name }}
                    </h3>
                  </div>
                  <span
                    :class="[
                      'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                      item.exam_status === 1
                        ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100'
                    ]"
                  >
                    {{ item.exam_status === 1 ? 'Active' : 'Draft' }}
                  </span>
                </div>
              <!-- Exam details (stacked rows on mobile) -->
              <div class="space-y-2 mb-4 text-sm">
                <div>
                  <span class="text-secondary-text dark:text-secondary-dark-text">Questions:</span>
                  <span class="ml-2 text-gray-900 dark:text-white font-medium">
                    {{ item.exam_content?.questions?.length || 0 }}
                  </span>
                </div>

                <div v-if="item.exam_content?.instructions">
                  <span class="text-secondary-text dark:text-secondary-dark-text">Instructions:</span>
                  <span class="ml-2 text-gray-900 dark:text-white font-medium">Available</span>
                </div>
              </div>

              <!-- Action buttons -->
              <div class="flex justify-end space-x-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                <!-- <button
                  @click="toggleExamStatus(item)"
                  :class="[
                    'p-2 rounded-lg transition-colors',
                    item.exam_status === 1
                      ? 'text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20'
                      : 'text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20'
                  ]"
                  :title="item.exam_status === 1 ? 'Deactivate' : 'Activate'"
                >
                  <LockOpen v-if="item.exam_status === 1" class="h-4 w-4" />
                  <Lock v-else class="h-4 w-4" />
                </button>
                <button
                  @click="editExam(item)"
                  class="p-2 rounded-lg text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  title="Edit"
                >
                  <Edit class="h-4 w-4" />
                </button> -->
                <button
                  @click="viewExam(item)"
                  class="p-2 rounded-lg text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                  title="View"
                >
                  <Eye class="h-4 w-4" />
                </button>
                <!-- <button
                  @click="deleteExam(item)"
                  class="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  title="Delete"
                >
                  <Trash2 class="h-4 w-4" />
                </button> -->
              </div>
            </div>
          </template>

          <!-- Empty state with custom actions -->
          <template #empty-actions>
            <div class="flex flex-col sm:flex-row gap-2 justify-center">
              <button
                v-if="hasActiveFilters"
                @click="clearAllFilters"
                class="btn btn-secondary btn-sm"
              >
                Clear Filters
              </button>
              <!-- <button
                @click="currentView = 'create'"
                class="btn btn-primary btn-sm"
              >
                Create First Exam
              </button> -->
            </div>
          </template>
        </DataTable>
        </div>
      </div>
    </transition>

    <!-- Delete Exam Modal -->
     <DeleteExam
      v-if="examToDelete"
      :is-visible="showDeleteModal"
      :exam="examToDelete"
      @success="onExamDeleted"
      @close="closeDeleteModal"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useTitle } from '@/composables/ui/useTitle'
import { useToastService } from '@/services/toast'
import { examService } from '@/services/examService'
import { LessonsService } from '@/services/lessons'
import type { Exam } from '@/services/examService'
import type { Lesson } from '@/services/lessons'
import DataTable from '@/components/common/DataTable.vue'
import CreateExamView from './CreateExamView.vue'
import EditExamView from './EditExamView.vue'
import DeleteExam from '@/components/modals/DeleteExam.vue'
import {
  ClipboardList,
  Plus,
  Upload,
  CheckCircle,
  Users,
  TrendingUp,
  BarChart3,
  AlertCircle,
  Edit,
  Eye,
  Trash2,
  Lock,
  LockOpen
} from 'lucide-vue-next'

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'Douglas Private Care Services')
const { setTitle } = useTitle(`Exams - ${appTitle.value}`)

const toastService = useToastService()
const router = useRouter()

// Helper function to maintain backward compatibility
const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
  if (type === 'success') {
    toastService.success(message)
  } else if (type === 'error') {
    toastService.error(message)
  } else {
    toastService.success(message) // Use success for info since the service may not have info
  }
}

// View state
const currentView = ref<'list' | 'create' | 'edit'>('list')
const loading = ref(false)
const error = ref('')

// Update document title when switching views
watch(currentView, (newView) => {
  if (newView === 'list') {
    setTitle(`Exams - ${appTitle.value}`)
  } else if (newView === 'create') {
    setTitle(`Create Exam - ${appTitle.value}`)
  } else if (newView === 'edit') {
    setTitle(`Edit Exam - ${appTitle.value}`)
  }
})

// Data with proper typing
const exams = ref<Exam[]>([])
const lessons = ref<Lesson[]>([])

// DataTable configuration
const displayExams = computed(() => exams.value)

const tableColumns = [
  { key: 'lesson', label: 'Lesson', sortable: false },
  { key: 'exam_name', label: 'Exam Details', sortable: true },
  { key: 'questions', label: 'Questions', align: 'center' as const, sortable: false },
  { key: 'status', label: 'Status', align: 'center' as const, sortable: true }
]

const sortableColumns = [
  { key: 'exam_name', label: 'Exam Name' },
  { key: 'created_at', label: 'Created Date' },
  { key: 'exam_status', label: 'Status' }
]

const mobileColumns = [
  { key: 'exam_name', label: 'Name' },
  { key: 'lesson', label: 'Lesson' },
  { key: 'status', label: 'Status' }
]

// Load filter state from localStorage
const loadFilterState = () => {
  try {
    const savedFilter = localStorage.getItem('manageExamsStatusFilter')
    return savedFilter || ''
  } catch (error) {
    console.error('Failed to load filter state from localStorage:', error)
    return ''
  }
}

const loadLessonFilterState = () => {
  try {
    const savedFilter = localStorage.getItem('manageExamsLessonFilter')
    return savedFilter || ''
  } catch (error) {
    console.error('Failed to load lesson filter state from localStorage:', error)
    return ''
  }
}

// Save filter state to localStorage
const saveFilterState = (value: string) => {
  try {
    localStorage.setItem('manageExamsStatusFilter', value)
  } catch (error) {
    console.error('Failed to save filter state to localStorage:', error)
  }
}

const saveLessonFilterState = (value: string) => {
  try {
    localStorage.setItem('manageExamsLessonFilter', value)
  } catch (error) {
    console.error('Failed to save lesson filter state to localStorage:', error)
  }
}

// Filtering and search state - initialize from localStorage
const statusFilter = ref(loadFilterState())
const lessonFilter = ref(loadLessonFilterState())

// Sorted lessons for the dropdown — only lessons that have exams and are not overview
const sortedLessons = computed(() => {
  const examLessonIds = new Set(
    exams.value
      .filter((e) => e.lesson_id != null)
      .map((e) => e.lesson_id)
  )
  return lessons.value
    .filter((m) => !m.is_overview && examLessonIds.has(m.lesson_id))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
})

// Watch for filter changes and save to localStorage
watch(statusFilter, (newValue) => {
  saveFilterState(newValue)
})

watch(lessonFilter, (newValue) => {
  saveLessonFilterState(newValue)
})

// Check if any filters are active
const hasActiveFilters = computed(() => {
  return statusFilter.value !== '' || lessonFilter.value !== ''
})

// Computed properties
const totalExams = computed(() => exams.value.length)
const activeExams = computed(() => exams.value.filter((exam: Exam) => exam.exam_status === 1).length)
const draftExams = computed(() => exams.value.filter((exam: Exam) => exam.exam_status === 0).length)
const averageQuestions = computed(() => {
  if (exams.value.length === 0) return 0
  const totalQuestions = exams.value.reduce((sum: number, exam: Exam) => sum + (exam.exam_content?.questions?.length || 0), 0)
  return Math.round(totalQuestions / exams.value.length)
})

// Methods
const fetchExams = async () => {
  try {
    loading.value = true
    error.value = ''
    const response = await examService.getAllExams()
    console.log('Raw API response:', response)
    
    // The API returns: { success: true, data: { data: [exams], ...pagination } }
    // So we need to access response.data.data.data for the actual exam array
    const examData = response.data?.data?.data || []
    console.log('Extracted exam data:', examData)
    
    exams.value = Array.isArray(examData) ? examData : []
    console.log('Final exams array:', exams.value)
  } catch (err: any) {
    console.error('Error fetching exams:', err)
    error.value = err.response?.data?.message || err.message || 'Failed to load exams'
    exams.value = [] // Ensure exams is always an array
    showToast('Failed to load exams', 'error')
  } finally {
    loading.value = false
  }
}

const fetchLessons = async () => {
  try {
    const lessonData = await LessonsService.getLessons()
    lessons.value = Array.isArray(lessonData) ? lessonData : []
  } catch (err: any) {
    console.error('Error fetching lessons:', err)
    lessons.value = []
  }
}

// DataTable event handlers
const handleSearch = (query: string) => {
  // The DataTable component handles search internally
  console.log('Search query:', query)
}

const handleSort = (sort: { field: string, direction: 'asc' | 'desc' }) => {
  // The DataTable component handles sorting internally
  console.log('Sort config:', sort)
}

const handleFilter = (filters: Record<string, any>) => {
  // Sync local refs when DataTable clears filters
  if (filters.exam_status !== undefined && statusFilter.value !== String(filters.exam_status || '')) {
    statusFilter.value = filters.exam_status ? String(filters.exam_status) : ''
  }
  if (filters.lesson_id !== undefined && lessonFilter.value !== String(filters.lesson_id || '')) {
    lessonFilter.value = filters.lesson_id ? String(filters.lesson_id) : ''
  }
}

const handlePageChange = (page: number) => {
  // The DataTable component handles pagination internally
  console.log('Page changed to:', page)
}

const handleItemsPerPageChange = (itemsPerPage: number) => {
  // The DataTable component handles items per page internally
  console.log('Items per page changed to:', itemsPerPage)
}

const clearAllFilters = () => {
  statusFilter.value = ''
  lessonFilter.value = ''
}

// Exam actions
const selectedExamId = ref<number>(0)

const toggleExamStatus = async (exam: any) => {
  try {
    const newStatus = exam.exam_status === 1 ? 0 : 1
    await examService.updateExamStatus(exam.exam_id, newStatus)
    exam.exam_status = newStatus
    showToast(`Exam ${newStatus === 1 ? 'activated' : 'drafted'} successfully!`, 'success')
  } catch (err: any) {
    console.error('Error updating exam status:', err)
    showToast(err.response?.data?.message || 'Failed to update exam status', 'error')
  }
}

const editExam = (exam: Exam) => {
  selectedExamId.value = exam.exam_id
  currentView.value = 'edit'
}

const viewExam = (exam: Exam) => {
  router.push({ name: 'exam-detail', params: { id: exam.exam_id } })
}

// Delete Exam
const showDeleteModal = ref(false)
const examToDelete = ref<Exam | null>(null)

const deleteExam = (exam: Exam) => {
  examToDelete.value = exam
  showDeleteModal.value = true
}

const onExamDeleted = async () => {
  showDeleteModal.value = false
  examToDelete.value = null
  await fetchExams()
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  setTimeout(() => {
    examToDelete.value = null
  }, 300)
}

const formatDate = (dateString: string) => {
  if (!dateString) return 'Unknown'
  
  const date = new Date(dateString)
  const now = new Date()
  const diffInMs = now.getTime() - date.getTime()
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
  
  if (diffInDays === 0) return 'Today'
  if (diffInDays === 1) return 'Yesterday'
  if (diffInDays < 7) return `${diffInDays} days ago`
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`
  return `${Math.floor(diffInDays / 365)} years ago`
}

// Format lesson display name as MMYY-HHA-[Lesson Title]
const formatLessonName = (lesson: any): string => {
  const name = lesson?.name || ''
  // Remove "Lesson X -" prefix if present
  const cleanedName = name.replace(/^Lesson\s+\d+\s*[-:\s]\s*/i, '').trim()

  // Try multiple date fields to find a valid date for MMYY prefix
  const candidateDates = [
    lesson?.time_setting?.start_date,
    lesson?.access_date_start,
    lesson?.added_at,
    lesson?.created_at
  ]

  const dateStr = candidateDates.find(d => d)
  if (!dateStr) return cleanedName

  try {
    const startDate = new Date(dateStr)
    if (Number.isNaN(startDate.getTime())) return cleanedName

    const month = String(startDate.getMonth() + 1).padStart(2, '0')
    const year = String(startDate.getFullYear()).slice(-2)
    return `${month}${year}-HHA-${cleanedName}`
  } catch {
    return cleanedName
  }
}

// Lifecycle
onMounted(() => {
  fetchExams()
  fetchLessons()
})

</script>

<style scoped>
.card {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
}

.dark .card {
  background-color: #1f2937;
  border-color: #374151;
}

.card-header {
  padding: 1.5rem 1.5rem 0 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
}

.dark .card-header {
  border-bottom-color: #374151;
}

.card-body {
  padding: 1.5rem;
}

.badge-success {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: #dcfce7;
  color: #166534;
}

.dark .badge-success {
  background-color: rgba(34, 197, 94, 0.3);
  color: #86efac;
}

.badge-warning {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: #fef3c7;
  color: #92400e;
}

.dark .badge-warning {
  background-color: rgba(245, 158, 11, 0.3);
  color: #fde047;
}

/* Modern transition animations for exam views */
.exam-view-enter-active {
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.exam-view-leave-active {
  transition: all 0.3s cubic-bezier(0.55, 0.055, 0.675, 0.19);
}

.exam-view-enter-from {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

.exam-view-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}

/* Additional smooth transitions for better UX */
.exam-view-enter-active * {
  transition-delay: 0.1s;
}

@media (max-width: 1660px) {
  /* Force the DataTable desktop table to hide and show the mobile cards */
  :deep(.hidden.xl\:block) {
    display: none !important;
  }

  :deep(.xl\:hidden) {
    display: block !important;
  }

  /* Truncate the lesson column (second column) like the lesson view reference */
  :deep(.min-w-full th:nth-child(2)),
  :deep(.min-w-full td:nth-child(2)) {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
